import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

import { JobApplicationForm } from "@/components/site/JobApplicationForm";
import { useLocale } from "@/lib/i18n";
import { listPublicJobAds } from "@/lib/recruiting.functions";
import { PageHero } from "../components/site/Page";
import { safeLoader } from "@/lib/safe-loader";


const title = "Lediga tjänster – jobba som lärare i Spanien | SIS Estepona";
const description =
  "Jobba på en svensk skola i Estepona/Marbella. Vi söker legitimerade lärare och pedagoger som vill vara med och forma en svensk skola i Spanien.";

export const Route = createFileRoute("/lediga-tjanster")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: absoluteUrl("/lediga-tjanster") },
      { property: "og:locale", content: "sv_SE" },
      ...ogImage(),
    ],
    links: seoLinks("/lediga-tjanster"),
  }),
  loader: () => safeLoader("sv.jobs", () => listPublicJobAds(), []),
  component: LedigaTjanster,
});

function AdList() {
  const ads = Route.useLoaderData();
  const locale = useLocale();
  if (ads.length === 0) return null;

  return (
    <section className="mx-auto max-w-3xl px-5 pb-4">
      <h2 className="text-2xl font-semibold">Aktuella tjänster</h2>
      <div className="mt-6 space-y-5">
        {ads.map((ad) => {
          const title = ad.title[locale] || ad.title.sv;
          const lead = ad.lead[locale] || ad.lead.sv;
          const body = ad.body[locale] || ad.body.sv;
          const fields = ad.customFields.filter((f) => f.public && f.label && f.value);
          return (
            <article key={ad.id} className="rounded-xl border border-border bg-card p-5 shadow-sm">
              <h3 className="text-xl font-semibold">{title}</h3>
              <p className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-sm text-muted-foreground">
                {ad.extent ? <span>{ad.extent}</span> : null}
                {ad.location ? <span>{ad.location}</span> : null}
                {ad.applyBy ? (
                  <span>Sök senast {new Date(ad.applyBy).toLocaleDateString("sv-SE", { timeZone: "Europe/Madrid" })}</span>
                ) : null}
              </p>
              {lead ? <p className="mt-3 text-base leading-relaxed text-foreground/90">{lead}</p> : null}
              {body ? (
                <p className="mt-3 whitespace-pre-line text-base leading-relaxed text-foreground/80">
                  {body}
                </p>
              ) : null}
              {ad.requirements.length > 0 ? (
                <>
                  <p className="mt-4 font-semibold">Krav</p>
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-foreground/85">
                    {ad.requirements.map((r) => (
                      <li key={r}>{r}</li>
                    ))}
                  </ul>
                </>
              ) : null}
              {ad.merits.length > 0 ? (
                <>
                  <p className="mt-4 font-semibold">Meriterande</p>
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-foreground/85">
                    {ad.merits.map((m) => (
                      <li key={m}>{m}</li>
                    ))}
                  </ul>
                </>
              ) : null}
              {fields.length > 0 ? (
                <dl className="mt-4 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                  {fields.map((f) => (
                    <div key={f.label} className="flex gap-2">
                      <dt className="text-muted-foreground">{f.label}:</dt>
                      <dd>{f.value}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}
              <div className="mt-5 flex flex-wrap items-center gap-3 text-sm">
                <a
                  href="#ansok"
                  className="inline-flex h-11 items-center rounded-md bg-primary px-5 font-semibold text-primary-foreground"
                >
                  Sök tjänsten
                </a>
                {ad.contact ? <span className="text-muted-foreground">{ad.contact}</span> : null}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function LedigaTjanster() {
  return (
    <>
      <PageHero
        eyebrow="Jobba hos oss"
        title="Lediga tjänster"
        intro="Att arbeta på Swedish International School i Spanien är en möjlighet för lärare och andra pedagogiska yrkesverksamma att vara med och forma en svensk skola från grunden."
      />

      <div className="pt-16" />
      <AdList />

      <section className="mx-auto max-w-3xl px-5 pb-16 pt-8">
        <div className="space-y-5 text-base leading-relaxed text-foreground/90">
          <p>
            Vår personal är skolans viktigaste byggsten. Vi strävar efter små grupper och en
            bemanning som ger utrymme att stödja varje elev, med nära kollegor och familjer, i en internationell miljö mellan Estepona och
            Marbella.
          </p>
          <h2 className="pt-4 text-2xl font-semibold">Vi söker dig som</h2>
          <ul className="list-disc space-y-2 pl-5">
            <li>Har svensk lärarlegitimation eller pedagogisk utbildning.</li>
            <li>Vill arbeta individanpassat med digitala verktyg som en naturlig del av lärandet.</li>
            <li>Trivs i en internationell miljö och med tät kontakt med elever och vårdnadshavare.</li>
            <li>Vill vara med och bygga upp en ny skola tillsammans med kollegor och ledning.</li>
          </ul>
          <h2 className="pt-4 text-2xl font-semibold">Att arbeta i Spanien</h2>
          <p>
            Skolan ligger i Cancelada mellan Estepona och Marbella, med närhet till både havet och
            bergen. För dig som flyttar hit innebär det en vardag med kort resväg, milt klimat större
            delen av året och ett internationellt sammanhang där svenska, spanska och engelska hörs
            sida vid sida. Vi hjälper gärna till med information om praktiska frågor som NIE,
            folkbokföring och bostadsområden – se våra{" "}
            <a className="text-primary link-quiet" href="/guider">guider</a> om att flytta till
            Spanien.
          </p>

          <h2 className="pt-4 text-2xl font-semibold">Rekryteringsprocessen</h2>
          <p>
            Vi läser ansökningar löpande. Efter en första kontakt bokar vi ett samtal – på plats eller
            digitalt – där vi går igenom din bakgrund, hur du arbetar och vad tjänsten innebär. Är det
            aktuellt ber vi om referenser och relevanta intyg. Behovet varierar över läsåret, så även
            om vi inte har en tjänst ledig just nu sparar vi gärna din ansökan.
          </p>

          <h2 className="pt-4 text-2xl font-semibold">Spontanansökan</h2>
          <p>
            Tveka inte att skicka in din ansökan eller höra av dig med frågor om hur det är att jobba
            hos oss.
          </p>
        </div>

        <p className="mt-8 text-sm text-foreground/70">
          Har du frågor innan du söker? Mejla oss på{" "}
          <a className="text-primary link-quiet" href="mailto:join@sweintschool.com">
            join@sweintschool.com
          </a>
          .
        </p>
      </section>

      <section id="ansok" className="border-t border-border bg-sand/40">
        <div className="mx-auto max-w-3xl px-5 py-16">
          <JobApplicationForm />
        </div>
      </section>
    </>
  );
}

